/*
 * Copyright (C) 2023 - present Marek Kuzora - All Rights Reserved.
 */


import { Client } from 'fierry/flux';

import SelectAlgorithm from 'shared/enum/select.algorithm';

import Behavior from 'shared/client/behavior';
import Url from 'shared/client/url';

import Galaxy from 'shared/query/galaxy';

import * as Array from 'fierry/util/array';
import * as Coords from 'shared/geo/coords';
import * as Shapes from 'shared/geo/shapes';


export default new Client ([ Url, Galaxy, Behavior ], class
{
  constructor (url, galaxy, behavior)
  {
    this.url = url;
    this.galaxy = galaxy;
    this.behavior = behavior;

    this.serverTargets = this.__getServerTargets__();
    this.behaviorTargets = this.__getBehaviorTargets__();
  }


  setCurrent (current)
  {
    this.url.pushArgs({ current, target: null });
  }


  setTargets (targets)
  {
    this.url.pushArgs({ targets });
  }


  clearAll ()
  {
    this.url.pushArgs({ current: null, target: null })
  }


  getCurrent ()
  {
    return this.galaxy ? this.galaxy.getLocation(this.url.getArg('current')) : null;
  }


  getRawTarget ()
  {
    const targets = this.getTargetsRepresentation();

    return targets.length ? targets[0].location : null
  }


  getTargetsRepresentation ()
  {
    if (this.galaxy == null)
    {
      return [];
    }

    return Object.entries(this.url.getArg('targets', {})).map( ([target, value]) =>
    {
      return { location: this.galaxy.getLocation(target), value };
    });
  }


  getFullTargets ()
  {
    return this.behavior.canSelect() ? this.behaviorTargets : this.serverTargets;
  }


  getBehaviorTargets ()
  {
    return this.behaviorTargets;
  }


  getBehaviorShape ()
  {
    const { location, value } = this.getTargetsRepresentation()[0];

    const { type, variant } = this.__getBehaviorDowngrade__(location,  value);

    return { type, variant };
  }


  getSelectStyle ()
  {
    return this.behavior.getSelectStyle();
  }


  __getBehaviorTargets__ ()
  {
    switch (this.behavior.getSelectAlgorithm())
    {
      case SelectAlgorithm.Multi: return this.__getBehaviorMultiTargets__();
      case SelectAlgorithm.Shape: return this.__getBehaviorShapeTargets__();
    }
  }


  __getBehaviorMultiTargets__ ()
  {
    return this.getTargetsRepresentation();
  }


  __getBehaviorShapeTargets__ ()
  {
    let collection = [];

    const mapCallback = (coords) =>
    {
      return { location: this.galaxy.getLocation(coords), value: 1 };
    };

    for (const { location, value } of this.getTargetsRepresentation())
    {
      collection = collection.concat(this.__getBehaviorDowngrade__(location, value).data.map(mapCallback))
    }

    return Array.unique(collection, (lhs, rhs) => lhs.location.equals(rhs.location));
  }


  __getBehaviorDowngrade__ (target, value)
  {
    const downgradeCallback = (coords) =>
    {
      const location = this.galaxy.getLocation(coords);

      if (location.getParentID() !== target.getParentID())
      {
        return true;
      }

      if (this.behavior.canSelectZone() && location.getZone())
      {
        return false;
      }

      if (this.behavior.canSelectPlatform() && location.getPlatform())
      {
        return false;
      }

      if (this.behavior.canSelectDock() && location.getDock())
      {
        return false;
      }

      if (this.behavior.canSelectGalaxy() && location.isEmpty())
      {
        return false;
      }

      return true;
    };

    const shape = this.behavior.getSelectShape(this.getCurrent(), this.galaxy);

    const variants = Shapes.getMaxVariants(target.getCoords(), this.getCurrent().getCoords(), shape);

    return Shapes.getDowngrade(target.getCoords(), variants[value - 1], downgradeCallback);
  }


  __getServerTargets__ ()
  {
    let collection = [];

    if (this.getCurrent() === null)
    {
      return collection;
    }

    for (const interaction of this.getCurrent().getInteractionsFrom())
    {
      const object = interaction.getObject();

      if (object.isAggressive() || object.isFutureTarget())
      {
        collection = collection.concat(interaction.getFullTarget());
      }
    }

    for (const projectile of this.getCurrent().getProjectilesFrom())
    {
      collection = collection.concat(projectile.getFullTarget());
    }

    return Array.unique(collection, (lhs, rhs) => lhs.location.equals(rhs.location));
  }


  getLocationInfo (location)
  {
    const isCurrent = this.isCurrent(location);
    const isTarget  = this.isTarget(location);
    const isOutOfRange = this.isOutOfRange(location);

    const navigation = (isCurrent || isTarget) ? this : null;

    return { isCurrent, isTarget, isOutOfRange, navigation };
  }


  isCurrent (zoneOrLocation)
  {
    return this.getCurrent() !== null && this.getCurrent().getID() === zoneOrLocation.getID();
  }


  isTarget (zoneOrLocation)
  {
    for (const target of this.getFullTargets())
    {
      if (target.location.getID() === zoneOrLocation.getID())
      {
        return true;
      }
    }

    return false;
  }


  isOutOfRange (location)
  {
    const range = this.behavior.getSelectRange();

    return range !== 0 && Coords.getDistance(location.getCoords(), this.getCurrent().getCoords()) > range;
  }


  onZoneClick (location)
  {
    if (this.behavior.canSelect())
    {
      if (this.__canAccessLocation__(location) && this.__canSelectInRange__(location))
      {
        this.setTargets(this.__getUpdatedTargets__(location.getID()));
      }
    }

    if (this.behavior.canNavigate())
    {
      if (this.__canAccessLocation__(location))
      {
        this.setCurrent(location.getID());
      }
      else
      {
        this.clearAll();
      }
    }
  }


  // TODO This currently blocks selecting galaxy in the non-active dock space. Non-active dock should be treated as isEmpty maybe ?
  __canAccessLocation__ (location)
  {
    // TODO Planet should not auto-create docks. We should install docks only for the sectors / zones that are active and working!
    return (location.getZone() && this.behavior.canSelectZone())
        || (location.getDock() && this.behavior.canSelectDock() && location.getDock().isActive())
        || (location.isEmpty() && this.behavior.canSelectGalaxy())
        || (location.getPlatform() && this.behavior.canSelectPlatform())
  }


  __canSelectInRange__ (location)
  {
    const maxRange = this.behavior.getSelectRange(this.galaxy);

    if (maxRange == 0)
    {
      return true;
    }

    const distance = Coords.getDistance(this.getCurrent().getCoords(), location.getCoords())

    return distance > 0 && distance <= maxRange;
  }


  __getUpdatedTargets__ (target)
  {
    let representation = { ...this.url.getArg('targets', {}) };

    if (target in representation)
    {
      return this.__updateExistingTarget__(target, representation);
    }

    return this.__selectNewTarget__(target, representation);
  }


  __updateExistingTarget__ (target, representation)
  {
    representation[target] = (representation[target] + 1) % (this.__getTargetValueCount__(target) + 1);

    if (representation[target] === this.__getInitialTargetValue__(target))
    {
      delete representation[target];
    }

    return representation;
  }


  __selectNewTarget__ (target, representation)
  {
    if (this.behavior.getSelectAlgorithm() !== SelectAlgorithm.Multi)
    {
      return { [target]: 1 };
    }

    representation[target] = (this.__getInitialTargetValue__(target) + 1) % (this.__getTargetValueCount__(target) + 1);

    return representation;
  }


  __getInitialTargetValue__ (target)
  {
    return this.behavior.getSelectInitialTargets(this.getCurrent(), this.galaxy)[target] || 0;
  }


  __getTargetValueCount__ (targetID)
  {
    const target = this.galaxy.getLocation(targetID);

    return this.behavior.getSelectValueCount(this.getCurrent(), target, this.galaxy);
  }


});
